/* eslint-disable react-refresh/only-export-components, max-lines */
import {
  DimeDataTable,
  DataTableColumnHeader,
  DimeButton,
  DimeChip,
  DimeRowActions,
  DimeRowActionsItem,
  DimeSeparator,
  type ColumnDef,
  type RowSelectionState,
  type SortingState,
} from '@nudool/design-system';
import { formatBusinessDateTimeFromIso } from '@nudool/shared';
import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

export const Route = createFileRoute('/design-system/data-table')({
  component: DataTableDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

type StatusVariant = 'success' | 'warning' | 'destructive' | 'secondary';

interface Order {
  id: string;
  customer: string;
  items: number;
  total: number;
  placedAt: string;
  status: string;
  statusVariant: StatusVariant;
}

const ORDERS: Order[] = [
  {
    id: 'SO-1001',
    customer: 'Acme Corp',
    items: 4,
    total: 2340.0,
    placedAt: '2026-09-14T09:15:00-07:00',
    status: 'Confirmed',
    statusVariant: 'success',
  },
  {
    id: 'SO-1002',
    customer: 'GlobalTech Ltd',
    items: 1,
    total: 580.5,
    placedAt: '2026-09-15T11:42:30-07:00',
    status: 'Pending',
    statusVariant: 'warning',
  },
  {
    id: 'SO-1003',
    customer: 'Orion Industries',
    items: 7,
    total: 9120.75,
    placedAt: '2026-09-16T08:03:12-07:00',
    status: 'Confirmed',
    statusVariant: 'success',
  },
  {
    id: 'SO-1004',
    customer: 'Apex Supplies',
    items: 3,
    total: 340.25,
    placedAt: '2026-09-16T14:32:05-07:00',
    status: 'Draft',
    statusVariant: 'secondary',
  },
];

const COLUMNS: ColumnDef<Order, unknown>[] = [
  {
    accessorKey: 'id',
    meta: { label: 'Order' },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Order" />,
    cell: ({ row }) => <span className="font-mono text-xs font-medium">{row.getValue('id')}</span>,
  },
  {
    accessorKey: 'customer',
    meta: { label: 'Customer' },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Customer" />,
    cell: ({ row }) => <span className="font-medium">{row.getValue('customer')}</span>,
  },
  {
    accessorKey: 'items',
    meta: { label: 'Items' },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Items" className="justify-end" />
    ),
    cell: ({ row }) => (
      <span className="block text-right tabular-nums text-muted-foreground">
        {row.getValue('items')}
      </span>
    ),
  },
  {
    accessorKey: 'total',
    meta: { label: 'Total', exportType: 'currency' },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Total" className="justify-end" />
    ),
    cell: ({ row }) => {
      const total = row.getValue('total') as number;
      return (
        <span className="block text-right tabular-nums">
          {total.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
        </span>
      );
    },
  },
  {
    accessorKey: 'placedAt',
    // Hidden by default (and so unexported) but still switchable from the Columns menu.
    meta: { label: 'Placed', defaultHidden: true, exportType: 'datetime' },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Placed" />,
    cell: ({ row }) => (
      <span className="tabular-nums text-muted-foreground">
        {formatBusinessDateTimeFromIso(row.getValue('placedAt') as string) ?? '—'}
      </span>
    ),
  },
  {
    accessorKey: 'status',
    meta: { label: 'Status' },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
    cell: ({ row }) => {
      const order = row.original;
      return <DimeChip variant={order.statusVariant}>{order.status}</DimeChip>;
    },
  },
  {
    id: 'avgItemPrice',
    // No accessor, so it is unsortable and dropped from exports without meta.exportValue.
    meta: {
      label: 'Avg / item',
      exportType: 'currency',
      exportValue: (row: Order) => Number((row.total / row.items).toFixed(2)),
    },
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Avg / item" className="justify-end" />
    ),
    cell: ({ row }) => (
      <span className="block text-right tabular-nums">
        {(row.original.total / row.original.items).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
        })}
      </span>
    ),
  },
];

const CUSTOMERS = ['Acme Corp', 'GlobalTech Ltd', 'Orion Industries', 'Apex Supplies'];
const STATUSES: { status: string; statusVariant: StatusVariant }[] = [
  { status: 'Confirmed', statusVariant: 'success' },
  { status: 'Pending', statusVariant: 'warning' },
  { status: 'Draft', statusVariant: 'secondary' },
];

// 45 rows so the footer has a short final page to render a range for.
const ALL_ORDERS: Order[] = Array.from({ length: 45 }, (_, index) => ({
  id: `SO-${2001 + index}`,
  customer: CUSTOMERS[index % CUSTOMERS.length]!,
  items: (index % 7) + 1,
  total: 250 + index * 137.5,
  placedAt: new Date(2026, 8, 1 + (index % 16), 9 + (index % 8), index % 60).toISOString(),
  ...STATUSES[index % STATUSES.length]!,
}));

/** A local stand-in for a server-paginated list, so the footer has real state to drive. */
function PaginatedDemo() {
  const [pageIndex, setPageIndex] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);
  const [selected, setSelected] = React.useState<RowSelectionState>({});
  const [sortBy, setSortBy] = React.useState<SortingState>([]);

  const lastPageIndex = Math.ceil(ALL_ORDERS.length / pageSize) - 1;
  const sorted = React.useMemo(() => {
    const active = sortBy[0];
    if (!active) return ALL_ORDERS;
    return [...ALL_ORDERS].sort((rowA, rowB) => {
      const valueA = rowA[active.id as keyof Order];
      const valueB = rowB[active.id as keyof Order];
      const order = valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
      return active.desc ? -order : order;
    });
  }, [sortBy]);
  const page = sorted.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize);

  return (
    <DimeDataTable
      columns={COLUMNS}
      data={page}
      striped
      showSelect
      selected={selected}
      onUpdateSelected={setSelected}
      canSelectRow={(order) => order.status !== 'Draft'}
      sortBy={sortBy}
      onUpdateSortBy={setSortBy}
      manualSorting
      exportFileName="orders"
      onRequestExportRows={() => Promise.resolve(sorted)}
      pageIndex={pageIndex}
      pageSize={pageSize}
      onPageSizeChange={(size) => {
        setPageSize(size);
        setPageIndex(0);
      }}
      totalCount={ALL_ORDERS.length}
      hasPreviousPage={pageIndex > 0}
      hasNextPage={pageIndex < lastPageIndex}
      onFirstPage={() => setPageIndex(0)}
      onPreviousPage={() => setPageIndex((current) => current - 1)}
      onNextPage={() => setPageIndex((current) => current + 1)}
      onLastPage={() => setPageIndex(lastPageIndex)}
    />
  );
}

function DataTableDocs() {
  const [isLoading, setIsLoading] = React.useState(false);

  return (
    <div>
      <h1 className="mb-1 text-3xl font-bold">Data Table</h1>
      <p className="mb-8 text-muted-foreground">
        Full-featured table built on TanStack Table with sorting, search, column visibility, row
        selection, pagination, and loading skeletons.
      </p>

      <DocsSection title="With local data">
        <div className="mb-3 flex items-center gap-2">
          <DimeButton
            variant={isLoading ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => setIsLoading((prev) => !prev)}
          >
            {isLoading ? 'Show data' : 'Simulate loading'}
          </DimeButton>
          <span className="text-sm text-muted-foreground">Toggle the loading skeleton state</span>
        </div>
        <DimeDataTable
          columns={COLUMNS}
          data={ORDERS}
          isLoading={isLoading}
          loadingRowCount={5}
          emptyMessage="No orders found."
          showViewOptions
          searchColumn="customer"
          searchPlaceholder="Search customer..."
          showExport={false}
        />
        <p className="mt-3 text-sm text-muted-foreground">
          Pass <code className="font-mono">columns</code> and{' '}
          <code className="font-mono">data</code> at minimum. The{' '}
          <code className="font-mono">Columns</code> visibility dropdown is on by default — it is
          the only way to hide a column now that the header menu is gone — so pass{' '}
          <code className="font-mono">showViewOptions={'{false}'}</code> on a table nobody would
          configure. <code className="font-mono">searchColumn</code> wires up the toolbar search to
          a specific accessor key. <code className="font-mono">Placed</code> carries{' '}
          <code className="font-mono">meta.defaultHidden</code>, so it starts hidden here and can be
          switched on from that menu, or with every other hidden column through its{' '}
          <code className="font-mono">Show All</code> item.
        </p>
      </DocsSection>

      <DocsSection title="Pagination footer, selection and striping">
        <PaginatedDemo />
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">pageIndex</code> + <code className="font-mono">pageSize</code>{' '}
          drive the <code className="font-mono">Showing X–Y</code> range, and{' '}
          <code className="font-mono">totalCount</code> adds the{' '}
          <code className="font-mono">of 45</code> suffix. Supplying{' '}
          <code className="font-mono">onPageSizeChange</code> renders the rows-per-page select
          (capped at 100, the server&apos;s page ceiling); supplying{' '}
          <code className="font-mono">onFirstPage</code>/
          <code className="font-mono">onLastPage</code> renders « and », which{' '}
          <code className="font-mono">showFirstLast={'{false}'}</code> removes again.{' '}
          <code className="font-mono">striped</code> shades alternate rows without overriding hover
          or the selected state. Selection is keyed by <code className="font-mono">getRowId</code>{' '}
          (defaulting to the row&apos;s <code className="font-mono">id</code>), so ticking a row,
          paging forward and paging back still points at the same record — and the counter reads{' '}
          <code className="font-mono">N selected</code>, with no fabricated denominator.{' '}
          <code className="font-mono">canSelectRow</code> marks rows that cannot join a bulk action
          (Draft orders here): their checkbox is disabled and &ldquo;Select all&rdquo; skips them.
        </p>
      </DocsSection>

      <DocsSection title="Sorting">
        <p className="mb-3 text-sm text-muted-foreground">
          Click a header label to cycle asc → desc → none. That label is the header&apos;s only
          control: there is no chevron menu, and Hide lives in the toolbar&apos;s Columns menu.
          Sorting a paginated list returns you to page 1 — a cursor points at a row in a specific
          ordering, so once the ordering changes there is no same place to stay in. The{' '}
          <code className="font-mono">&lt;th&gt;</code> carries{' '}
          <code className="font-mono">aria-sort</code>, and a sortable header renders the same
          typography and left edge as a plain one. <code className="font-mono">Avg / item</code> is
          derived, so it sets <code className="font-mono">enableSorting: false</code> rather than
          offering a sort that could only reorder the loaded page. The table above is{' '}
          <code className="font-mono">manualSorting</code>, which is what every paginated list must
          pass.
        </p>
      </DocsSection>

      <DocsSection title="Export">
        <DimeDataTable columns={COLUMNS} data={ALL_ORDERS.slice(0, 4)} exportFileName="orders" />
        <p className="mt-3 text-sm text-muted-foreground">
          The Export control (CSV and Excel) is on by default — pass{' '}
          <code className="font-mono">showExport={'{false}'}</code> to suppress it. It writes the
          currently visible columns in their current order, always omitting the selection and{' '}
          <code className="font-mono">actions</code> columns. This table has no{' '}
          <code className="font-mono">onRequestExportRows</code>, so it exports its loaded rows; the
          paginated demo above supplies one and exports all 45.{' '}
          <code className="font-mono">Avg / item</code> has no accessor behind it and would export
          blank without <code className="font-mono">meta.exportValue</code>, which returns the raw
          number; <code className="font-mono">meta.exportType</code> then formats it, so the CSV
          reads <code className="font-mono">$585.00</code> and the XLSX cell is both currency-
          formatted and summable. Export is <strong>permission-gated</strong>: pass{' '}
          <code className="font-mono">exportDisabledReason</code> and the control renders disabled
          with that reason as its tooltip — <code className="font-mono">useListTable</code> sets it
          from <code className="font-mono">importsExport:export</code>, so every list route is
          covered. On a selection-capable table with rows ticked, only those records are exported,
          including any selected on a page that is no longer loaded. Excel is written through a
          dynamically imported writer, so it never loads until the first XLSX export.
        </p>
      </DocsSection>

      <DocsSection title="Sticky header">
        <DimeDataTable
          columns={COLUMNS}
          data={ALL_ORDERS}
          maxBodyHeight="viewport"
          showExport={false}
        />
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">maxBodyHeight="viewport"</code> caps the table at whatever
          viewport height is left below it, measured live, and sticks the header row to the top of
          that box while the body scrolls inside it. It is a{' '}
          <code className="font-mono">max-height</code>, not a{' '}
          <code className="font-mono">height</code> — a short table still sizes to its own content
          rather than stretching to fill the page. Shared with{' '}
          <code className="font-mono">DimeTreeTable</code>&apos;s prop of the same name, so both
          tables size identically.
        </p>
      </DocsSection>

      <DocsSection title="Empty state">
        <DimeDataTable columns={COLUMNS} data={[]} emptyMessage="No orders match your search." />
        <p className="mt-3 text-sm text-muted-foreground">
          Empty <code className="font-mono">data</code> renders the{' '}
          <code className="font-mono">emptyMessage</code> spanning all columns.
        </p>
      </DocsSection>

      <DocsSection title="Sub-rows">
        <DimeDataTable
          columns={COLUMNS}
          data={ORDERS}
          showExport={false}
          renderSubRow={(order) =>
            order.id === ORDERS[0]?.id ? (
              <p className="text-sm text-muted-foreground">Leave at the loading dock.</p>
            ) : null
          }
        />
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">renderSubRow</code> renders its result as a full-width row
          right after its row, or nothing for <code className="font-mono">null</code>, so an
          expandable note never adds height to the rows without one. Sub-rows follow their row
          through sorting and search, and are never exported.
        </p>
      </DocsSection>

      <DocsSection title="Phone rows">
        <div className="max-w-md">
          <DimeDataTable
            columns={COLUMNS}
            data={ORDERS}
            showExport={false}
            renderMobileRow={(order) => (
              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs font-medium">{order.id}</span>
                  <DimeChip variant={order.statusVariant}>{order.status}</DimeChip>
                </div>
                <span className="font-medium">{order.customer}</span>
                <span className="text-sm tabular-nums text-muted-foreground">
                  {order.items} items ·{' '}
                  {order.total.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                </span>
              </div>
            )}
            renderSubRow={(order) =>
              order.id === ORDERS[0]?.id ? (
                <p className="text-sm text-muted-foreground">Leave at the loading dock.</p>
              ) : null
            }
          />
        </div>
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">renderMobileRow</code> swaps the header and cells for one
          full-width card per row while the table&apos;s own container measures under 768px — this
          one sits in a narrow box, so it shows cards at any viewport. With no header to click, each
          sortable column becomes a sort button in the toolbar, and the Columns menu is dropped. A{' '}
          <code className="font-mono">renderSubRow</code> result still follows its card. Cards do
          not render row selection, so don&apos;t pair this with{' '}
          <code className="font-mono">showSelect</code>. Without{' '}
          <code className="font-mono">renderMobileRow</code> a table never measures its width.
        </p>
      </DocsSection>

      <DocsSection title="Row actions">
        <div className="flex flex-col gap-2">
          {ORDERS.map((order) => (
            <div
              key={order.id}
              className="flex items-center justify-between rounded-md border border-border px-4 py-3 text-sm"
            >
              <span className="font-mono text-xs font-medium">{order.id}</span>
              <span className="font-medium">{order.customer}</span>
              <DimeChip variant={order.statusVariant}>{order.status}</DimeChip>
              <DimeRowActions label={`Actions for ${order.id}`}>
                <DimeRowActionsItem>View</DimeRowActionsItem>
                <DimeRowActionsItem>Edit</DimeRowActionsItem>
                <DimeRowActionsItem destructive>Cancel</DimeRowActionsItem>
              </DimeRowActions>
            </div>
          ))}
        </div>
        <p className="mt-3 text-sm text-muted-foreground">
          Place <code className="font-mono">DimeRowActions</code> in the rightmost cell of each row.
          Pass <code className="font-mono">destructive</code> to{' '}
          <code className="font-mono">DimeRowActionsItem</code> for irreversible actions.
        </p>
      </DocsSection>
    </div>
  );
}
